import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import { TextComponent } from 'design/text/textComponents';
import {
  PersonalInfoFormValues,
  PersonalInfoFormViolation,
} from 'types/personalInfoTypes';
import { createPersonalFormViolationLense } from 'utils/validatePersonalInfoFormValues';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';

type Props = {
  values: PersonalInfoFormValues;
  violations: PersonalInfoFormViolation[];
  disabled: boolean;
  onFirstNameChange(nextValue: string): void;
  onMiddleNameChange(nextValue: string): void;
  onLastNameChange(nextValue: string): void;
  onPhoneChange(nextValue: string): void;
  onPositionChange(nextValue: string): void;
};

const FIRST_NAME_MAX_LENGTH = 40;
const MIDDLE_NAME_MAX_LENGTH = 40;
const LAST_NAME_MAX_LENGTH = 40;
const PHONE_MAX_LENGTH = 19;
const POSITION_MAX_LENGTH = 255;

export const ProdStackManagerPersonalInfoFieldset = (props: Props) => {
  const {
    disabled,
    values,
    violations,
    onFirstNameChange,
    onMiddleNameChange,
    onLastNameChange,
    onPhoneChange,
    onPositionChange,
  } = props;

  const {
    inputValidationRequired,
    prodStackManager: { clientInfoForm: ownDictionary },
  } = useIntlDictionary();

  const lastNameHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'LastNameMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const lastNameIsInvalid = violations.some((violation) =>
    createPersonalFormViolationLense(violation).isLastNameViolation()
  );

  const firstNameHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'FirstNameMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const firstNameIsInvalid = violations.some((violation) =>
    createPersonalFormViolationLense(violation).isFirstNameViolation()
  );

  const middleNameHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'MiddleNameMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const middleNameIsInvalid = violations.some((violation) =>
    createPersonalFormViolationLense(violation).isMiddleNameViolation()
  );

  const phoneHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'PhoneMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const phoneIsInvalid = violations.some((violation) =>
    createPersonalFormViolationLense(violation).isPhoneViolation()
  );

  const positionHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'PositionMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const positionIsInvalid = violations.some((violation) =>
    createPersonalFormViolationLense(violation).isPositionViolation()
  );

  return (
    <FieldsetLayoutComponent>
      <FloatingLabelComponent
        lifted={!!values.lastName}
        invalid={lastNameIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.lastNameInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {lastNameHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="lastName"
          autoFocus
          value={values.lastName}
          disabled={disabled}
          invalid={lastNameIsInvalid}
          maxLength={LAST_NAME_MAX_LENGTH}
          onValueChange={onLastNameChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.firstName}
        invalid={firstNameIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.firstNameInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {firstNameHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="firstName"
          value={values.firstName}
          disabled={disabled}
          invalid={firstNameIsInvalid}
          maxLength={FIRST_NAME_MAX_LENGTH}
          onValueChange={onFirstNameChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.middleName}
        invalid={middleNameIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.middleNameInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {middleNameHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="middleName"
          value={values.middleName}
          disabled={disabled}
          invalid={middleNameIsInvalid}
          maxLength={MIDDLE_NAME_MAX_LENGTH}
          onValueChange={onMiddleNameChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.phone}
        invalid={phoneIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.phoneInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {phoneHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="phone"
          value={values.phone}
          disabled={disabled}
          invalid={phoneIsInvalid}
          maxLength={PHONE_MAX_LENGTH}
          onValueChange={onPhoneChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.position}
        invalid={positionIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.positionInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {positionHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="position"
          value={values.position}
          disabled={disabled}
          invalid={positionIsInvalid}
          maxLength={POSITION_MAX_LENGTH}
          onValueChange={onPositionChange}
        />
      </FloatingLabelComponent>
    </FieldsetLayoutComponent>
  );
};
